/**
 * What the builder knows about the adviser's practice (it steers every proposal and the test reviewer),
 * the documents read so far (only their name and size are kept — never the file), and the reference
 * agent the adviser's agent is compared with. All of it is the adviser's to correct.
 *
 * API: POST /agent-builder/practice {facts} · DELETE /agent-builder/practice/:index ·
 *      PUT /agent-builder/settings {referenceCountry}
 */
import React, { useState } from 'react';
import { useToast } from '../../Context/ToastContext';
import { Box, Row, Muted, Help, SmallBtn, PrimaryBtn, Input, Select, Empty, Bar, errText, fmtWhen } from './styles';

const AREA_LABEL = { pathways: 'Routes', knowledgeCategories: 'Topics', strategies: 'Strategies', ruleSets: 'Rules', teachings: 'Corrections', practice: 'About your practice' };

export default function PracticePanel({ api, data, reload }) {
  const { showToast } = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const facts = data?.practice || [];

  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post('/agent-builder/practice', { facts: [text.trim()] }); setText(''); await reload(); } catch (e) { showToast(errText(e, 'Could not save'), 'error'); } finally { setBusy(false); }
  };
  const remove = async (i) => {
    try { await api.delete(`/agent-builder/practice/${i}`); await reload(); } catch (e) { showToast(errText(e, 'Could not remove'), 'error'); }
  };
  const setCountry = async (v) => {
    try { await api.put('/agent-builder/settings', { referenceCountry: v || null }); await reload(); showToast('Reference agent updated', 'success'); } catch (e) { showToast(errText(e, 'Could not change it'), 'error'); }
  };

  return (
    <div>
      <Box>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>How complete is your agent</div>
        {(data?.readiness?.areas || []).map(a => (
          <div key={a.area} style={{ display: 'grid', gridTemplateColumns: '140px 1fr 70px', gap: 10, alignItems: 'center', margin: '6px 0' }}>
            <Muted>{AREA_LABEL[a.area] || a.area}</Muted>
            <Bar $pct={a.pct}><div /></Bar>
            <Muted style={{ textAlign: 'right' }}>{a.have}{a.reference != null ? ` / ${a.reference}` : ''}</Muted>
          </div>
        ))}
        <Help>Compared with iMaven’s reference agent{data?.reference ? `: ${data.reference.name}` : ''}. Your agent may legitimately differ — this shows coverage, not quality (tests show quality).</Help>
        <Row style={{ marginTop: 8 }}>
          <Muted>Compare with:</Muted>
          <Select value={data?.referenceCountry || ''} onChange={e => setCountry(e.target.value)}>
            <option value="">Your country (automatic)</option>
            <option value="canada">Canada</option>
            <option value="usa">United States</option>
            <option value="israel">Israel</option>
            <option value="general">General (no country)</option>
          </Select>
        </Row>
      </Box>

      <Box>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>What the builder knows about your practice</div>
        <Help style={{ marginBottom: 8 }}>The builder saves these as you talk. Remove anything wrong — it shapes every proposal and how tests are judged.</Help>
        {facts.length === 0 ? <Empty style={{ padding: 12 }}>Nothing yet — tell the builder about your practice.</Empty> : facts.map(f => (
          <Row key={f.index} style={{ justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.06)', padding: '6px 0', flexWrap: 'nowrap' }}>
            <span style={{ fontSize: 13, lineHeight: 1.45 }}>{f.text}</span>
            <SmallBtn onClick={() => remove(f.index)} title="Remove">✕</SmallBtn>
          </Row>
        ))}
        <Row style={{ marginTop: 8, flexWrap: 'nowrap' }}>
          <Input placeholder="Add a fact, e.g. “I do not take cases outside Ontario”" value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') add(); }} />
          <PrimaryBtn disabled={busy || !text.trim()} onClick={add}>Add</PrimaryBtn>
        </Row>
      </Box>

      <Box>
        <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Documents you shared</div>
        <Help style={{ marginBottom: 8 }}>Files are read once and not kept: only knowledge is taken into proposals, never names or personal details. Up to {data?.limits?.maxFiles || 5} files of {data?.limits?.maxFileMb || 20} MB at a time — drop them into the chat.</Help>
        {(data?.documents || []).length === 0 ? <Muted>None yet.</Muted> : data.documents.map((d, i) => (
          <Row key={i} style={{ justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.06)', padding: '6px 0' }}>
            <span style={{ fontSize: 13 }}>{d.name}</span>
            <Muted>{d.proposals} proposal{d.proposals === 1 ? '' : 's'} · {fmtWhen(d.at)}</Muted>
          </Row>
        ))}
      </Box>
    </div>
  );
}
